import { Injectable, OnDestroy } from '@angular/core';
import { SettingsService } from '@stockcare-services/settings-service/settings.service';
import { Settings } from '@stockcare-helpers/settings';
import { filter, switchMap } from 'rxjs/operators';
import { Subject, Subscription, interval, Observable } from 'rxjs';
import { DeviceService } from '@stockcare-services/devices-service/devices.service';
import { AuthenticationStateService } from '@stockcare-services/authentication/authentication-state.service';
import { TheatrePatientService } from './theatre-patient-service';

@Injectable({
  providedIn: 'root',
})
export class TheatreRefreshService implements OnDestroy {
  private theatreRefreshInterval = new Subject();
  private theatreNeedsToBeRefreshedSubject = new Subject<boolean>();
  subscriptions = new Subscription();
  isLoggedIn: boolean;
  isBusyLoading = false;

  constructor(
    private settingsService: SettingsService,
    private deviceService: DeviceService,
    private authenticationStateService: AuthenticationStateService,
    private theatrePatientService: TheatrePatientService
  ) {}

  initialize() {
    console.log('Starting theatre refresh service');
    this.subscribeToSettingsChanged();
    this.startPolling();
    this.getSettings();
    this.subscribeToLoggedIn();
  }

  private subscribeToLoggedIn() {
    this.subscriptions.add(
      this.authenticationStateService.isLoggedIn().subscribe((isLoggedIn) => {
        this.isLoggedIn = isLoggedIn;
      })
    );
  }

  private subscribeToSettingsChanged() {
    this.settingsService.settingsUpdated
      .pipe(filter((settings) => this.settingsService.hasSettingBeenUpdated(Settings.THEATRE_REFRESH_INTERVAL, settings)))
      .subscribe(() => {
        this.getSettings();
      });
  }

  private getSettings() {
    const theatreRefreshIntervalSetting = this.settingsService.getSettingValue(Settings.THEATRE_REFRESH_INTERVAL) * 1000;

    this.theatreRefreshInterval.next(!theatreRefreshIntervalSetting ? 300000 : theatreRefreshIntervalSetting);
  }

  private startPolling() {
    this.theatreRefreshInterval
      .pipe(
        switchMap((theatreRefreshIntervalSetting: number) =>
          interval(!theatreRefreshIntervalSetting ? 300000 : theatreRefreshIntervalSetting)
        )
      )
      .subscribe(() => {
        this.getPatients();
        this.publishUpdate();
      });
  }

  publishUpdate() {
    this.theatreNeedsToBeRefreshedSubject.next(true);
  }

  theatreNeedsToBeRefreshed(): Observable<boolean> {
    return this.theatreNeedsToBeRefreshedSubject.asObservable();
  }

  private getPatients() {
    if (this.isLoggedIn) {
      return;
    }

    if (this.isBusyLoading) {
      return;
    }

    const device = this.deviceService.getDeviceFromLocalStorage();

    if (!device) {
      return;
    }

    this.theatrePatientService.getAllTheatrePatientsByPhysicalLocation(device.physicalLocationId).subscribe(
      () => {
        this.isBusyLoading = false;
      },
      () => (this.isBusyLoading = false)
    );
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }
}
